/**
 * 选择服务实现：管理拾取结果的选择集，支持 set/add/remove/toggle/clear 操作，
 * 变更时通过 EventBus 发布 selection.changed 事件
 */

import type { EventBus } from '@/runtime/EventBus'
import type { PickResult } from '@/shared/types/core'
import type { SelectionService } from '@/shared/types/services'

/** 选择服务实现类 */
export class SelectionServiceImpl implements SelectionService {
  /** 当前选中项列表 */
  private items: PickResult[] = []

  /** 事件总线 */
  private eventBus: EventBus

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus
  }

  /** 获取当前选中项的只读副本 */
  get selected(): ReadonlyArray<PickResult> {
    return this.snapshot()
  }

  /** 是否有选中项 */
  get hasSelection(): boolean {
    return this.items.length > 0
  }

  /**
   * 替换整个选择集。
   *
   * @param items - 新的选中项列表，去重后覆盖旧集合
   */
  set(items: PickResult[]): void {
    const normalized = this.normalize(items)
    if (this.isSameSet(normalized)) return
    const previous = this.snapshot()
    this.items = normalized
    this.emitChanged(previous)
  }

  /**
   * 追加选中项（跳过已存在的 assetId）。
   *
   * @param items - 待追加的选中项列表
   */
  add(items: PickResult[]): void {
    const toAdd = this.normalize(items).filter(
      (item) => !this.items.some((s) => s.assetId === item.assetId)
    )
    if (toAdd.length === 0) return
    const previous = this.snapshot()
    this.items = this.items.concat(toAdd)
    this.emitChanged(previous)
  }

  /**
   * 按条件移除选中项。
   *
   * @param predicate - 返回 true 表示从选择集中移除
   */
  remove(predicate: (item: PickResult) => boolean): void {
    const filtered = this.items.filter((item) => !predicate(item))
    if (filtered.length === this.items.length) return
    const previous = this.snapshot()
    this.items = filtered
    this.emitChanged(previous)
  }

  /** 清空选择集 */
  clear(): void {
    if (this.items.length === 0) return
    const previous = this.snapshot()
    this.items = []
    this.emitChanged(previous)
  }

  /**
   * 切换选中状态：按 assetId 匹配，存在则移除，不存在则添加。
   *
   * @param item - 待切换的拾取结果
   */
  toggle(item: PickResult): void {
    const exists = this.items.some((selected) => selected.assetId === item.assetId)
    const previous = this.snapshot()
    if (exists) {
      this.items = this.items.filter((selected) => selected.assetId !== item.assetId)
    } else {
      this.items = this.items.concat(item)
    }
    this.emitChanged(previous)
  }

  /** 释放资源，清空选择集 */
  dispose(): void {
    this.items = []
  }

  /** 创建当前选中项的快照副本 */
  private snapshot(): PickResult[] {
    return this.items.slice()
  }

  /** 规范化输入，按首个出现顺序去重 assetId */
  private normalize(items: ReadonlyArray<PickResult>): PickResult[] {
    const assetIds = new Set<string>()
    return items.filter((item) => {
      if (assetIds.has(item.assetId)) {
        return false
      }
      assetIds.add(item.assetId)
      return true
    })
  }

  /** 发布 selection.changed 事件 */
  private emitChanged(previous: PickResult[]): void {
    this.eventBus.emit('selection.changed', {
      selected: this.items.slice(),
      previous,
    })
  }

  /** 判断传入列表是否与当前选择集相同（按 assetId 顺序比较） */
  private isSameSet(items: ReadonlyArray<PickResult>): boolean {
    if (items.length !== this.items.length) return false
    for (let i = 0; i < items.length; i++) {
      if (items[i].assetId !== this.items[i].assetId) return false
    }
    return true
  }
}
